(ns admin.utils
  (:require [applied-science.js-interop :as j]
            [clojure.string :as str]
            [kyber.constants :as kc]))

(defn vec-remove
  "remove elem in coll"
  [coll pos]
  (vec (concat (subvec coll 0 pos) (subvec coll (inc pos)))))

(defn join-by-prefix
  "`join-by-prefix` joins grouped inputs be a specified prefix"
  [object prefix]
  ;; v could be anything but will be true/false if it is a checkbox
  (reduce (fn [memo [k v]]
            (let [key-name (name k)]
              (if (str/starts-with? key-name (str prefix "_"))
                (if (true? v)
                  (let [[key-name* key-value*]
                        (str/split key-name #"_")]

                    (update memo
                            (keyword key-name*)
                            #(if (some? %)
                               (conj % key-value*)
                               [key-value*])))
                  memo)
                (assoc memo k v))))
          {}
          object))

(defn ->learner-url
  [{:keys [curriculum activity query-string]}]
  (cond-> (str "/curriculum/" curriculum)
    (some? activity)
    (str "/activity/" activity)

    (some? query-string)
    (str query-string)))

(defn ->admin-url
  [{:keys [activity-uuid curriculum-uuid]}]
  (cond-> "/admin"
    (some? curriculum-uuid)
    (str "/curriculum/" curriculum-uuid)

    (some? activity-uuid)
    (str "/activity/" activity-uuid "/build")))

(defn model->uri
  [{:keys [model status activity-uuid curriculum-uuid]}]
  (let [base-uri (->admin-url {:activity-uuid activity-uuid
                               :curriculum-uuid curriculum-uuid})
        label-status (if (= status "fail")
                       "Add "
                       "Edit ")]
    (case model
      "boosts" {:uri (str base-uri "/boosts")
                :label (str label-status "Boosts")}
      "moment" {:uri (str base-uri "/moments")
                :label (str label-status "Moments")}
      "resource" {:uri (str base-uri "/resources")
                  :label (str label-status "Resources")}
      "assessment" {:uri (str base-uri "/assessments/questions")
                    :label (str label-status "Assessments")}
      "evaluation" {:uri (str base-uri "/evaluation/questions")
                    :label (str label-status "Evaluation")}
      nil)))

(defn sanitize-s3-filename
  [file-name]
  (str/replace file-name #"[^\w\.]" "_"))

(defn is-editing-moment?
  "A moment is in an editing state"
  [{:keys [edit-state]}]
  (some? edit-state))

(defn is-editing-question?
  "A question is in an editing state"
  [{:keys [edit-state]}]
  (some? edit-state))

(defn get-moment-uuid
  "If a moment is new (not persisted) it'll have a temp-uuid"
  [{:keys [uuid temp-uuid]}]
  (or uuid temp-uuid))

(defn get-editing-moment
  "Returns a moment containing `edit-state`"
  [moments]
  (->> moments
       (filter #(-> % :edit-state some?))
       (first)))

(defn is-temp-moment?
  [{:keys [temp-uuid uuid]}]
  (and (some? temp-uuid) (nil? uuid)))

(defn question->question-type-display
  [question]
  (case (-> question :question/type keyword)
    :rating-scale "Rating Scale Question"
    :free-response "Free Response Question"
    :single-answer "Single Answer Question"
    :multiple-answer "Multiple Answer Question"

    (js/console.error "Question type is not supported.")))

(defn question->question-correctness-display [question]
  (case (-> question :question/has-correctness)
            true "Graded - "
            false "Ungraded - "))

(defn moment->type-display
  [{:moment/keys [type question]} editor-state]
  (case type
    "note" "Note"
    "question" (cond
                 (some? question) (str (question->question-correctness-display question)
                                       (question->question-type-display question))
                ;;  Using new-question to display the question-type in the panel-title before the user has saved the question.
                 (some? (@editor-state :new-question)) 
                 (str (question->question-correctness-display (@editor-state :new-question))
                      (question->question-type-display (@editor-state :new-question))) 
                 :else "Question")
    nil))

(defn question-type->edit-state
  [question-type]
  (case question-type
    :single-answer-graded {:question/type :single-answer
                           :question/has-correctness? true}
    :single-answer-ungraded {:question/type :single-answer
                             :question/has-correctness? false}
    :multiple-answer-graded {:question/type :multiple-answer
                             :question/has-correctness? true}
    :multiple-answer-ungraded {:question/type :multiple-answer
                               :question/has-correctness? false}
    :free-response {:question/type :free-response
                    :question/has-correctness? false}
    :rating-scale {:question/type :rating-scale
                   :question/has-correctness? false}

    (js/console.error "Question type is not supported")))

(defn supports-poll-results?
  [question-type]
  (contains? #{:single-answer :multiple-answer} (keyword question-type)))

(defn supported-highlighted-resource?
  "Returns true if the resource can be highlighted in a moment"
  [resource]
  (let [file-type (or (:resource/file-type resource)
                      (:file-type resource))]
    (contains? #{:audio :image :pdf :video} (keyword file-type))))

(defn remove-other-choice-option
  "Other choice options can be present on single or multi select
  questions that allow users to provide their own answer.

  Removes it from the choice set"
  [choices]
  (filterv #(not= kc/custom-answer-uuid (:question.choice/uuid %)) choices))

(defn add-other-choice-option
  "Other choice options can be present on single or multi select
  questions that allow users to provide their own answer.

  Adds a new one to the choice set"
  [choices]
  (conj choices {:question.choice/uuid kc/custom-answer-uuid
                 :question.choice/body "Other"}))

(defn has-other-choice-option?
  "Other choice options can be present on single or multi select
  questions that allow users to provide their own answer.

  Checks to see if one exists in the choice set"
  [choices]
  (->> choices
       (filter #(= kc/custom-answer-uuid (:question.choice/uuid %)))
       (seq)
       (boolean)))

(defn reorder-coll
  "Re-orders a collection placing the start item in place of the end item.
  Depending on the direction, the item will end up in front of or behind the
  the end item."
  [start end coll]
  (let [source (nth coll start)
        coll-len (count coll)]
    (if (< start end)
      (vec
       (concat
        (subvec coll 0 start)
        (subvec coll (inc start) (inc end))
        [source]
        (subvec coll (.min js/Math (inc end) coll-len))))
      (vec
       (concat
        (subvec coll 0 end)
        [source]
        (subvec coll end start)
        (subvec coll (.min js/Math (inc start) coll-len)))))))

(defn filter-selected-resources
  "Takes the difference between the two sets of resources (both structured
  as dictionaries)"
  [{:keys [all-resources selected-resources]}]
  (let [selected-resource-uuids (keys selected-resources)]
    (apply dissoc all-resources selected-resource-uuids)))

(def  annotation-tools
  [["AnnotationCreateTextHighlight" "Text Highlight" :highlighter]
   ["AnnotationCreateSticky"        "Sticky Note" :sticky-note]
   ["AnnotationCreateRectangle"     "Rectangle" :vector-square]])

(def annotation-tools-dict
  (into {} (map (fn [[tool-name :as tool]]
                  {tool-name tool})
                annotation-tools)))

(defn annotation->tool
  "Given an annotation, returns tool information"
  [annotation]
  (if-let [tool (get annotation-tools-dict (j/get annotation :ToolName))]
    tool
    (js/console.error
     (str "Annotation type not supported: "
          (j/get annotation :ToolName)))))

(defn valid-video-moment-location?
  "Takes a collection of existing moments, and returns true
   if an edited moment is in a valid position"
  [{:keys [moments uuid display-at hide-at]}]
  (->> moments
       (remove #(= uuid (:moment/uuid %)))
       (sort-by :moment/display-at)
       (filter #(or
                 ;; Moments can not display in the middle of whispers
                 (and (>= display-at (:moment/display-at %))
                      (<= display-at (:moment/hide-at %)))
                 (and (>= hide-at (:moment/display-at %))
                      (<= hide-at (:moment/hide-at %)))
                 ;; Whispers cannot span over other moments
                 (and (<= display-at (:moment/display-at %))
                      (>= hide-at (:moment/hide-at %)))))
       (empty?)))
